import { isCodexApiMockEnabled, isHybridMockEnabled } from './utils/codexEnv';

// ── Feature Flags ──────────────────────────────────────────────
const FEATURE_FLAGS_ALL_ENABLED = {
  chat: true,
  stocks: true,
  llmRules: true,
  feedback: true,
  journalPage: true,
  pipelineWebhook: true,
  analytics: true,
  analyticsLlmRules: true,
  analyticsWebhook: true,
  tradeHistoryWebhook: true,
  userApiKeys: true,
  appOpenAiKey: true,
  appAnthropicKey: false,
  loaded: true,
};

// ── User & Account ─────────────────────────────────────────────
const mockUser = {
  uid: 'codex-user',
  email: 'codex@local.dev',
  displayName: 'Codex Trader',
  emailVerified: true,
};

const mockUserInfo = {
  userId: 'codex-user',
  username: 'Codex Trader',
  email: 'codex@local.dev',
  createdAt: [2024, 6, 15, 10, 30, 0, 0],
  updatedAt: [2025, 3, 1, 8, 0, 0, 0],
  active: true,
  admin: false,
  emailVerified: true,
  lastLogin: [2025, 3, 1, 8, 0, 0, 0],
  picture: '',
  dailyMessagesLimit: 50,
  currentAiModel: 'gpt-4o',
  premium: true,
  premiumAiModel: 'gpt-4o',
  premiumMessagesLimit: 100,
  alphaAccess: true,
};

const mockAccount = {
  equity: 100000,
  currency: 'USD',
};

// ── Subscription & Usage ───────────────────────────────────────
const mockSubscriptionStatus = {
  tierName: 'Pro',
  staticLimit: 100,
  staticUsed: 12,
  canValidateStatic: true,
  resetAt: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
};

const mockUsageSummary = {
  totalCost: 1.42,
  totalTokens: 28500,
  validationCount: 12,
};

const mockMySubscription = {
  tierId: 'pro',
  tierName: 'Pro',
  validationLimit: 100,
  messageLimit: 100,
  webhookLimit: 10,
  analyticsLimit: 50,
};

const mockMyUsage = {
  validationUsed: 12,
  validationLimit: 100,
  messageUsed: 8,
  messageLimit: 100,
  webhookUsed: 1,
  webhookLimit: 10,
  analyticsUsed: 3,
  analyticsLimit: 50,
};

// ── ConceptGate ────────────────────────────────────────────────
const mockTrade = {
  id: 'trade-codex-1',
  instrument: 'EURUSD',
  tradeType: 'LONG',
  entryPrice: 1.0825,
  stopLoss: 1.079,
  takeProfit: 1.092,
  createdAt: new Date().toISOString(),
  latestValidation: {
    id: 'validation-codex-1',
    status: 'PASSED',
    totalDurationMs: 1250,
    totalTokens: 420,
    estimatedCostUsd: 0.02,
    startedAt: new Date(Date.now() - 60 * 1000).toISOString(),
    completedAt: new Date().toISOString(),
    ruleExecutions: [
      {
        id: 'rule-exec-1',
        userRuleId: 'user-rule-1',
        libraryRuleId: 'lib-rule-1',
        ruleName: 'Risk/Reward Ratio',
        ruleType: 'HARD_CODED',
        status: 'PASSED',
        message: 'Risk/reward ratio meets minimum threshold.',
        suggestedFix: null,
        detailsJson: { actual_value: 2.4, threshold_value: 2 },
        durationMs: 320,
        executionOrder: 1,
      },
      {
        id: 'rule-exec-2',
        userRuleId: 'user-rule-2',
        libraryRuleId: 'lib-rule-2',
        ruleName: 'Max Daily Risk',
        ruleType: 'HARD_CODED',
        status: 'FAILED',
        message: 'Daily risk exceeds configured threshold.',
        suggestedFix: 'Reduce position size or widen stop loss.',
        detailsJson: { actual_value: 2.1, threshold_value: 1.5 },
        durationMs: 410,
        executionOrder: 2,
      },
    ],
  },
};

const mockLibraryRules = [
  { id: 'lib-rule-1', name: 'Risk/Reward Ratio', description: 'Ensure minimum risk/reward ratio is met before entering.', ruleType: 'HARD_CODED', category: 'RISK', enabled: true },
  { id: 'lib-rule-2', name: 'Max Daily Risk', description: 'Daily risk must remain within account limits.', ruleType: 'HARD_CODED', category: 'RISK', enabled: true },
  { id: 'lib-rule-3', name: 'Spread Check', description: 'Validate spread is within acceptable range.', ruleType: 'HARD_CODED', category: 'EXECUTION', enabled: true },
  { id: 'lib-rule-4', name: 'Trend Alignment', description: 'Entry must align with higher timeframe trend.', ruleType: 'HARD_CODED', category: 'TECHNICAL', enabled: true },
  { id: 'lib-rule-5', name: 'Event Proximity', description: 'No trades within 30 min of high-impact news.', ruleType: 'HARD_CODED', category: 'NEWS', enabled: true },
];

const mockUserRules = [
  { id: 'user-rule-1', libraryRuleId: 'lib-rule-1', enabled: true, ruleType: 'HARD_CODED' },
  { id: 'user-rule-2', libraryRuleId: 'lib-rule-2', enabled: true, ruleType: 'HARD_CODED' },
  { id: 'user-rule-3', libraryRuleId: 'lib-rule-3', enabled: true, ruleType: 'HARD_CODED' },
  { id: 'user-rule-4', libraryRuleId: 'lib-rule-4', enabled: false, ruleType: 'HARD_CODED' },
  { id: 'user-rule-5', libraryRuleId: 'lib-rule-5', enabled: true, ruleType: 'HARD_CODED' },
];

const mockPipelines = [
  { id: 'pipeline-1', name: 'Default Pipeline', active: true, createdAt: new Date(Date.now() - 7 * 86400000).toISOString(), rules: ['user-rule-1', 'user-rule-2', 'user-rule-3'] },
  { id: 'pipeline-2', name: 'Conservative', active: false, createdAt: new Date(Date.now() - 14 * 86400000).toISOString(), rules: ['user-rule-1', 'user-rule-2', 'user-rule-5'] },
];

const mockValidationPipeline = {
  validations: [
    { id: 'validation-codex-1', status: 'PASSED', instrument: 'EURUSD', createdAt: new Date(Date.now() - 5 * 60 * 1000).toISOString() },
    { id: 'validation-codex-2', status: 'FAILED', instrument: 'GBPUSD', createdAt: new Date(Date.now() - 2 * 60 * 1000).toISOString() },
    { id: 'validation-codex-3', status: 'PASSED', instrument: 'USDJPY', createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString() },
  ],
};

const mockSettings = {
  defaultModel: 'gpt-4o',
  instructions: 'Focus on risk management. Always check spread before entry.',
};

// ── Chat ───────────────────────────────────────────────────────
const mockChats = [
  { chat_id: 'chat-1', chat_name: 'EUR/USD Analysis', createdAt: new Date(Date.now() - 3600000).toISOString(), updatedAt: new Date(Date.now() - 1800000).toISOString() },
  { chat_id: 'chat-2', chat_name: 'GBP/USD Setup', createdAt: new Date(Date.now() - 86400000).toISOString(), updatedAt: new Date(Date.now() - 43200000).toISOString() },
  { chat_id: 'chat-3', chat_name: 'Weekly Review', createdAt: new Date(Date.now() - 172800000).toISOString(), updatedAt: new Date(Date.now() - 86400000).toISOString() },
];

const mockMessages = [
  { id: 'msg-1', chatId: 'chat-1', role: 'user', content: 'What is the current trend on EUR/USD H4?', createdAt: new Date(Date.now() - 3600000).toISOString() },
  { id: 'msg-2', chatId: 'chat-1', role: 'assistant', content: 'Based on the H4 chart, EUR/USD is in a **bullish trend**. Key observations:\n\n1. Price is above the 50 EMA and 200 EMA\n2. Higher highs and higher lows pattern intact\n3. RSI at 62 - room for further upside\n\nKey levels to watch:\n- **Support**: 1.0790 (recent swing low)\n- **Resistance**: 1.0880 (previous high)\n\nThe trend favors long entries on pullbacks to the 1.0800-1.0820 zone.', createdAt: new Date(Date.now() - 3540000).toISOString() },
];

const mockModels = [
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'openai', premium: false },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'openai', premium: false },
  { id: 'claude-3-5-sonnet', name: 'Claude 3.5 Sonnet', provider: 'anthropic', premium: true },
];

// ── Journal ────────────────────────────────────────────────────
const mockJournalAccounts = [
  { account_id: 1, name: 'Main Account', broker: 'Oanda', currency: 'USD', initialBalance: 10000, active: true },
  { account_id: 2, name: 'Demo Account', broker: 'IC Markets', currency: 'USD', initialBalance: 50000, active: true },
];

const mockJournalEntries = {
  entries: [
    { entry_id: 1, title: 'EUR/USD Long', instrument: 'EURUSD', trade_type: 'LONG', entry_price: 1.0820, exit_price: 1.0870, profit_loss: 250, return_percentage: 2.5, entry_size: 1.0, trade_date: new Date(Date.now() - 3600000).toISOString(), notes: 'Clean breakout above resistance. Held to target.', account_id: 1, method_analytics: 'Breakout', source: 'Manual' },
    { entry_id: 2, title: 'GBP/USD Short', instrument: 'GBPUSD', trade_type: 'SHORT', entry_price: 1.2650, exit_price: 1.2700, profit_loss: -150, return_percentage: -1.5, entry_size: 0.5, trade_date: new Date(Date.now() - 7200000).toISOString(), notes: 'Stopped out. News event moved price against position.', account_id: 1, method_analytics: 'Reversal', source: 'Manual' },
    { entry_id: 3, title: 'USD/JPY Long', instrument: 'USDJPY', trade_type: 'LONG', entry_price: 149.50, exit_price: 150.20, profit_loss: 420, return_percentage: 4.2, entry_size: 1.5, trade_date: new Date(Date.now() - 86400000).toISOString(), notes: 'Trend continuation. Great R:R setup.', account_id: 1, method_analytics: 'Trend', source: 'Manual' },
    { entry_id: 4, title: 'EUR/GBP Short', instrument: 'EURGBP', trade_type: 'SHORT', entry_price: 0.8580, exit_price: 0.8560, profit_loss: 100, return_percentage: 1.0, entry_size: 0.8, trade_date: new Date(Date.now() - 172800000).toISOString(), notes: 'Mean reversion play at upper range.', account_id: 1, method_analytics: 'Range', source: 'Manual' },
    { entry_id: 5, title: 'AUD/USD Long', instrument: 'AUDUSD', trade_type: 'LONG', entry_price: 0.6520, exit_price: 0.6490, profit_loss: -90, return_percentage: -0.9, entry_size: 0.6, trade_date: new Date(Date.now() - 259200000).toISOString(), notes: 'False breakout. Cut loss quickly.', account_id: 1, method_analytics: 'Breakout', source: 'Manual' },
  ],
  total: 5,
  limit: 20,
  offset: 0,
};

const mockJournalStatistics = {
  totalTrades: 5,
  winningTrades: 3,
  losingTrades: 2,
  winRate: 60.0,
  totalProfitLoss: 530,
  averageWin: 256.67,
  averageLoss: -120.0,
  profitFactor: 2.14,
  largestWin: 420,
  largestLoss: -150,
  averageRR: 1.8,
  averageHoldTime: '4h 30m',
  currentStreak: 1,
  bestStreak: 2,
  worstStreak: 1,
};

// ── Analytics ──────────────────────────────────────────────────
const mockAnalyticsModules = [
  { id: 'mod-1', name: 'Multi-Timeframe Analysis', description: 'AI-powered analysis across H1, H4, and D1 timeframes.', category: 'AI', premium: false, enabled: true },
  { id: 'mod-2', name: 'Correlation Scanner', description: 'Scan for correlated currency pairs and divergences.', category: 'TECHNICAL', premium: false, enabled: true },
  { id: 'mod-3', name: 'News Impact Analysis', description: 'AI assessment of upcoming economic events on your positions.', category: 'AI', premium: true, enabled: true },
];

const mockAnalyticsPipelines = [
  { id: 'apipe-1', name: 'Daily Analysis', active: true, modules: ['mod-1', 'mod-2'], createdAt: new Date(Date.now() - 604800000).toISOString() },
  { id: 'apipe-2', name: 'Pre-Trade Check', active: false, modules: ['mod-1', 'mod-3'], createdAt: new Date(Date.now() - 1209600000).toISOString() },
];

// ── Chart Data ─────────────────────────────────────────────────
const buildMockCandles = (count = 60) => {
  const now = Math.floor(Date.now() / 1000);
  const candles = [];
  let price = 1.082;
  for (let i = count; i > 0; i -= 1) {
    const time = now - i * 60;
    const open = price;
    const high = open + 0.0015;
    const low = open - 0.0012;
    const close = open + (i % 2 === 0 ? 0.0008 : -0.0006);
    price = close;
    candles.push({ time, mid: { o: open.toFixed(5), h: high.toFixed(5), l: low.toFixed(5), c: close.toFixed(5) }, volume: 1000 + i * 5 });
  }
  return candles;
};

// ── Response Helpers ───────────────────────────────────────────
const jsonResponse = (data, status = 200) =>
  Promise.resolve(new Response(JSON.stringify(data), { status, headers: { 'Content-Type': 'application/json' } }));

const normalizePath = (url) => {
  const parsed = new URL(url, window.location.origin);
  const apiIndex = parsed.pathname.indexOf('/api/v2/');
  if (apiIndex === -1) return null;
  const path = parsed.pathname.slice(apiIndex + '/api/v2/'.length);
  return `${path}${parsed.search}`;
};

// ── Hybrid Mode: only mock auth-related endpoints ──────────────
// When REACT_APP_HYBRID_MOCK=true, only intercept login/auth calls.
// All other API calls pass through to the real backend.
const HYBRID_MOCK_PATHS = [
  'copilot/users/login',
  'config/features/resolved',
  'config/features',
];

const isHybridMockPath = (method, path) => {
  if (!isHybridMockEnabled) return false;
  return HYBRID_MOCK_PATHS.some(p => path.startsWith(p));
};

// ── Route Matching ─────────────────────────────────────────────
const matchHandler = (method, path) => {
  // Config & feature flags
  if (method === 'GET' && path.startsWith('config/features/resolved')) return jsonResponse(FEATURE_FLAGS_ALL_ENABLED);
  if (method === 'GET' && path.startsWith('config/features')) return jsonResponse(FEATURE_FLAGS_ALL_ENABLED);
  if (method === 'GET' && path.startsWith('config/my-subscription')) return jsonResponse(mockMySubscription);
  if (method === 'GET' && path.startsWith('config/my-usage')) return jsonResponse(mockMyUsage);
  if (method === 'GET' && path.startsWith('config/tiers')) return jsonResponse([
    { id: 'free', name: 'Free', price: 0, validationLimit: 5, messageLimit: 10 },
    { id: 'basic', name: 'Basic', price: 9.99, validationLimit: 50, messageLimit: 50 },
    { id: 'pro', name: 'Pro', price: 29.99, validationLimit: 100, messageLimit: 100 },
  ]);

  // Auth
  if (method === 'POST' && path === 'copilot/users/login') {
    return jsonResponse({
      admin: false,
      needsVerification: false,
      alphaEnabled: false,
      alphaAccess: true,
      waitlistEnabled: false,
      user: mockUserInfo,
    });
  }
  if (method === 'GET' && path.startsWith('copilot/mymessage/limit')) return jsonResponse({ remaining: 42, limit: 100 });

  // Chat
  if (method === 'GET' && path === 'copilot/chats') return jsonResponse(mockChats);
  if (method === 'POST' && path === 'copilot/chat') return jsonResponse({ chat_id: `chat-${Date.now()}`, chat_name: 'New Chat' });
  if (method === 'GET' && /copilot\/chat\/.*\/messages/.test(path)) return jsonResponse(mockMessages);
  if (method === 'DELETE' && /copilot\/chat\//.test(path)) return jsonResponse(null, 204);

  // Models
  if (method === 'GET' && path.startsWith('copilot/models')) return jsonResponse(mockModels);

  // Chart data
  if (method === 'GET' && path.startsWith('copilot/graph')) return jsonResponse(buildMockCandles(120));

  // Stock search
  if (method === 'GET' && path.startsWith('copilot/stock/search')) {
    return jsonResponse([
      { symbol: 'EURUSD', name: 'Euro / US Dollar', type: 'forex' },
      { symbol: 'GBPUSD', name: 'British Pound / US Dollar', type: 'forex' },
      { symbol: 'USDJPY', name: 'US Dollar / Japanese Yen', type: 'forex' },
    ]);
  }

  // ConceptGate - Account
  if (method === 'GET' && path.startsWith('conceptgate/account')) return jsonResponse(mockAccount);
  if (method === 'PUT' && path.startsWith('conceptgate/account')) return jsonResponse(mockAccount);

  // ConceptGate - Usage
  if (method === 'GET' && path.startsWith('conceptgate/usage/summary')) return jsonResponse(mockUsageSummary);
  if (method === 'GET' && path.startsWith('conceptgate/config/llm-instructions')) return jsonResponse(mockSettings);
  if (method === 'GET' && path.startsWith('conceptgate/subscription')) return jsonResponse(mockSubscriptionStatus);

  // ConceptGate - Trade
  if (method === 'GET' && path.startsWith('conceptgate/trade')) return jsonResponse(mockTrade);
  if (method === 'POST' && path.startsWith('conceptgate/trade')) return jsonResponse(mockTrade);

  // ConceptGate - Rules
  if (method === 'GET' && path.startsWith('conceptgate/library/rules')) return jsonResponse(mockLibraryRules);
  if (method === 'GET' && path.startsWith('conceptgate/user/rules')) return jsonResponse(mockUserRules);

  // ConceptGate - Pipelines (specific paths first, then generic with startsWith to catch ?instrument=...)
  if (method === 'GET' && path.startsWith('conceptgate/pipelines/active')) return jsonResponse(mockPipelines[0]);
  if (method === 'GET' && /conceptgate\/pipelines\/[^/]+\/rules/.test(path)) return jsonResponse(mockUserRules.slice(0, 3));
  if (method === 'GET' && /conceptgate\/pipelines\/[^/?]+$/.test(path)) return jsonResponse(mockPipelines[0]);
  if (method === 'GET' && path.startsWith('conceptgate/pipelines')) return jsonResponse(mockPipelines);
  if (method === 'POST' && path.startsWith('conceptgate/pipelines')) return jsonResponse(mockPipelines[0]);

  // ConceptGate - Validations
  if (method === 'GET' && path.startsWith('conceptgate/validations')) return jsonResponse(mockValidationPipeline);
  if (method === 'POST' && path.startsWith('conceptgate/validate')) return jsonResponse({ error: 'Validation disabled in mock mode' }, 403);

  // ConceptGate - History
  if (method === 'GET' && path.startsWith('conceptgate/history')) return jsonResponse([]);

  // Journal - Accounts
  if (method === 'GET' && path === 'trader-journal/accounts') return jsonResponse(mockJournalAccounts);
  if (method === 'POST' && path === 'trader-journal/accounts') return jsonResponse({ ...mockJournalAccounts[0], account_id: Date.now() });
  if (method === 'GET' && /trader-journal\/accounts\/[^/]+$/.test(path)) return jsonResponse(mockJournalAccounts[0]);

  // Journal - Entries (return array directly, as the component expects Array.isArray(response))
  if (method === 'GET' && path.startsWith('trader-journal/entries')) return jsonResponse(mockJournalEntries.entries);
  if (method === 'POST' && path === 'trader-journal/entries') return jsonResponse(mockJournalEntries.entries[0]);
  if (method === 'GET' && /trader-journal\/entries\/[^/]+$/.test(path)) return jsonResponse(mockJournalEntries.entries[0]);
  if (method === 'PUT' && /trader-journal\/entries\/[^/]+$/.test(path)) return jsonResponse(mockJournalEntries.entries[0]);
  if (method === 'DELETE' && /trader-journal\/entries\/[^/]+$/.test(path)) return jsonResponse(null, 204);

  // Journal - Statistics
  if (method === 'GET' && path.startsWith('trader-journal/statistics')) return jsonResponse(mockJournalStatistics);

  // Analytics - Modules
  if (method === 'GET' && path === 'analytics/library/modules') return jsonResponse(mockAnalyticsModules);

  // Analytics - Pipelines (specific paths first, then generic with startsWith to catch ?instrument=...)
  if (method === 'GET' && path.startsWith('analytics/pipelines/active')) return jsonResponse(mockAnalyticsPipelines[0]);
  if (method === 'GET' && /analytics\/pipelines\/[^/]+\/modules/.test(path)) return jsonResponse(mockAnalyticsModules.slice(0, 2));
  if (method === 'GET' && /analytics\/pipelines\/[^/?]+$/.test(path)) return jsonResponse(mockAnalyticsPipelines[0]);
  if (method === 'GET' && path.startsWith('analytics/pipelines')) return jsonResponse(mockAnalyticsPipelines);
  if (method === 'POST' && path.startsWith('analytics/pipelines')) return jsonResponse(mockAnalyticsPipelines[0]);

  // Analytics - History
  if (method === 'GET' && path.startsWith('analytics/history')) return jsonResponse([]);

  // Alpha
  if (method === 'GET' && path.startsWith('alpha/status')) return jsonResponse({ alphaEnabled: false });

  // User API keys
  if (method === 'GET' && path === 'user/api-keys') return jsonResponse([]);
  if (method === 'GET' && path === 'user/api-keys/status') return jsonResponse({ openai: false, anthropic: false });

  // Feedback
  if (method === 'POST' && path.startsWith('feedback')) return jsonResponse({ id: 'feedback-1', status: 'received' });

  // System
  if (method === 'GET' && path.startsWith('sys/version')) return jsonResponse({ version: 'mock-local' });
  if (method === 'GET' && path.startsWith('sys/health')) return jsonResponse({ status: 'UP' });

  // Default: return empty object
  return jsonResponse({});
};

// ── Setup ──────────────────────────────────────────────────────
export const setupMockApi = () => {
  if ((!isCodexApiMockEnabled && !isHybridMockEnabled) || typeof window === 'undefined') {
    return () => {};
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const requestUrl = typeof input === 'string' ? input : input.url;
    const method = (init.method || (typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    const normalizedPath = normalizePath(requestUrl);

    if (!normalizedPath) {
      // In hybrid mode, block Firebase external calls that cause 407 errors
      if (isHybridMockEnabled) {
        const url = requestUrl.toString();
        if (url.includes('firebaseinstallations.googleapis.com') ||
            url.includes('identitytoolkit.googleapis.com') ||
            url.includes('firebaselogging') ||
            url.includes('firebase-settings')) {
          return new Response('{}', { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
      }
      return originalFetch(input, init);
    }

    // In hybrid mode, only intercept auth-critical paths; pass rest to real backend
    if (isHybridMockEnabled && !isCodexApiMockEnabled) {
      if (!isHybridMockPath(method, normalizedPath)) {
        return originalFetch(input, init);
      }
    }

    return matchHandler(method, normalizedPath);
  };

  return () => {
    window.fetch = originalFetch;
  };
};
